/**
 * Pure defaults and merge helpers for the app-wide settings row. This module
 * has no database import so the Dexie v2 upgrade step (`src/db/db.ts`) can
 * use it without creating a db <-> domain import cycle.
 */
import type { AiSettings, CloudSettings, DeviceIdentity, KoofrSettings, OnboardingState, Settings, UiSettings } from '../types';

/**
 * Google closed `gemini-2.5-flash-lite` to new users: its metadata endpoint
 * still resolves, so "Testa anslutningen" passes, while generateContent
 * answers 404 "no longer available to new users" on every receipt. A default
 * has to be one a fresh key can actually call.
 */
export const DEFAULT_GEMINI_MODEL = 'gemini-3.5-flash-lite';

export const DEFAULT_AI_SETTINGS: Readonly<AiSettings> = Object.freeze({
  provider: 'gemini',
  baseUrl: '',
  apiKey: '',
  model: DEFAULT_GEMINI_MODEL,
  geminiPlan: 'free',
  autoReadInbox: true,
  parallel: 1,
});

export const KOOFR_BASE_URL = 'https://app.koofr.net';

export const DEFAULT_KOOFR_SETTINGS: Readonly<KoofrSettings> = Object.freeze({
  baseUrl: KOOFR_BASE_URL,
  email: '',
  appPassword: '',
  mountId: '',
});

/** Drive stays the default: one click, and per-file scope. */
export const DEFAULT_CLOUD_SETTINGS: Readonly<CloudSettings> = Object.freeze({
  provider: 'drive',
  koofr: DEFAULT_KOOFR_SETTINGS,
});

export const DEFAULT_UI_SETTINGS: Readonly<UiSettings> = Object.freeze({
  advancedMode: false,
  theme: 'system',
  dismissedCallouts: [] as string[],
});

export const DEFAULT_ONBOARDING: Readonly<OnboardingState> = Object.freeze({ step: 0 });

export const DEFAULT_SETTINGS: Readonly<Settings> = Object.freeze({
  id: 'settings',
  activeCompanyId: null,
  /** Minted on first run by `ensureDeviceIdentity`, never by a default. */
  device: null,
  ai: DEFAULT_AI_SETTINGS,
  cloud: DEFAULT_CLOUD_SETTINGS,
  ui: DEFAULT_UI_SETTINGS,
  onboarding: DEFAULT_ONBOARDING,
});

/** Deep-partial patch: each nested group is merged, arrays are replaced. */
export interface SettingsPatch {
  activeCompanyId?: Settings['activeCompanyId'];
  /** Replaced wholesale: an identity is not something to patch a field of. */
  device?: DeviceIdentity;
  ai?: Partial<AiSettings>;
  cloud?: Partial<Omit<CloudSettings, 'koofr'>> & { koofr?: Partial<KoofrSettings> };
  ui?: Partial<UiSettings>;
  onboarding?: Partial<OnboardingState>;
}

function stripUndefined<T extends object>(o: T): T {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(o)) if (v !== undefined) out[k] = v;
  return out as T;
}

/**
 * Fill every missing field of a (possibly v1 or partial) settings row with
 * the defaults. Existing values, including the AI provider, are kept.
 */
export function withSettingsDefaults(raw: Partial<Settings> | Record<string, unknown> | undefined): Settings {
  const r = (raw ?? {}) as Partial<Settings>;
  return {
    id: 'settings',
    activeCompanyId: r.activeCompanyId ?? null,
    device: r.device ?? null,
    ai: { ...DEFAULT_AI_SETTINGS, ...stripUndefined(r.ai ?? {}) },
    cloud: {
      ...DEFAULT_CLOUD_SETTINGS,
      ...stripUndefined(r.cloud ?? {}),
      koofr: { ...DEFAULT_KOOFR_SETTINGS, ...stripUndefined(r.cloud?.koofr ?? {}) },
    },
    ui: { ...DEFAULT_UI_SETTINGS, dismissedCallouts: [...DEFAULT_UI_SETTINGS.dismissedCallouts], ...stripUndefined(r.ui ?? {}) },
    onboarding: { ...DEFAULT_ONBOARDING, ...stripUndefined(r.onboarding ?? {}) },
  };
}

/** Pure merge used by `updateSettings`; exported for tests and the upgrade step. */
export function applySettingsPatch(current: Settings, patch: SettingsPatch): Settings {
  return {
    id: 'settings',
    activeCompanyId: patch.activeCompanyId !== undefined ? patch.activeCompanyId : current.activeCompanyId,
    device: patch.device ?? current.device,
    ai: { ...current.ai, ...stripUndefined(patch.ai ?? {}) },
    cloud: {
      ...current.cloud,
      ...(patch.cloud?.provider ? { provider: patch.cloud.provider } : {}),
      koofr: { ...current.cloud.koofr, ...stripUndefined(patch.cloud?.koofr ?? {}) },
    },
    ui: { ...current.ui, ...stripUndefined(patch.ui ?? {}) },
    onboarding: { ...current.onboarding, ...stripUndefined(patch.onboarding ?? {}) },
  };
}
